import { useQuery } from "@tanstack/react-query";
import { toastError, toastSuccess, toastWarning } from "helpers";
import { createLogger } from "logging";
import { OneDriveSyncAgendaItem } from "models/OneDriveSyncAgendaItem";
import { SyncActionsType, SyncActionType } from "models/SyncActionsType";
import { useApi } from "providers";
import { useMeeting } from "providers";
import { useTranslations } from "providers";
import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";

import { OneDriveSyncContext } from "./OneDriveSyncContext";

const logger = createLogger("OneDriveSyncProvider");

export const OneDriveSyncProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();
  const { meetingId } = useMeeting();
  const { oneDriveApi } = useApi();
  const [selectDocumentId, setSelectDocumentId] = useState<number>(-1);
  const [agendaItemActions, setAgendaItemActions] = useState<SyncActionsType>({});
  const [documentActions, setDocumentActions] = useState<SyncActionsType>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);

  // prettier-ignore
  const query  = useQuery({
    queryKey: [`Meeting|${meetingId}|OneDriveSync`],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const data = await oneDriveApi.getByMeeting(Number(meetingId), signal);
      initDocumentActions(data.agendaItems ?? []);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: !!meetingId,
  });
  const { refetch } = query;
  const oneDriveViewModel = query.data;
  const isLoading = query.isPending;
  const folder = useMemo(() => oneDriveViewModel?.folder, [oneDriveViewModel]);
  const agendaItems = useMemo(() => oneDriveViewModel?.agendaItems || [], [oneDriveViewModel]);

  const initDocumentActions = (agendaItems: OneDriveSyncAgendaItem[]) => {
    setAgendaItemActions([]);

    // use server computed defaults for file actions
    const results: SyncActionsType = [];
    agendaItems.forEach((agendaItem) => {
      agendaItem.files.forEach((file) => {
        if (file.id) {
          results[file.id] = file.action || "None";
        }
      });
    });

    logger.debug(`initDocumentActions:=`, results);
    setDocumentActions(results);
  };

  const setDocumentAction = useCallback((id: number, action: SyncActionType) => {
    setDocumentActions((prev) => ({ ...prev, [id]: action }));
  }, []);

  const updateAgendaItemActions = useCallback(
    (agendaItem: OneDriveSyncAgendaItem, action: SyncActionType) => {
      const newAgendaItemActions = { ...agendaItemActions };
      const id = agendaItem.agendaItem.id;
      newAgendaItemActions[id] = action;
      setAgendaItemActions(newAgendaItemActions);
      logger.debug(`updateAgendaItemActions(): `, id, action);
    },
    [agendaItemActions]
  );

  const updateDocumentActions = useCallback(
    (agendaItem: OneDriveSyncAgendaItem, action: SyncActionType) => {
      const newDocumentActions = { ...documentActions };
      const files = agendaItem?.files || [];
      files.forEach((file) => {
        const id = file.id;
        logger.debug(`updateDocumentActions(): `, id, action);
        if (id) newDocumentActions[id] = action;
      });
      setDocumentActions(newDocumentActions);
    },
    [documentActions]
  );

  const setAgendaItemAction = useCallback(
    (id: number, action: SyncActionType) => {
      logger.debug(`setAgendaItemAction(): `, id, action);

      const agendaItem = agendaItems.find((x) => x.agendaItem.id === id);
      logger.debug(`setAgendaItemAction(): `, agendaItem);

      if (agendaItem) {
        updateAgendaItemActions(agendaItem, action);
        updateDocumentActions(agendaItem, action);
      }
    },
    [agendaItems, updateAgendaItemActions, updateDocumentActions]
  );

  const onExecuteSync = useCallback(() => {
    if (!meetingId) return;

    setIsSubmitting(true);

    oneDriveApi
      .synchronize(Number(meetingId), documentActions)
      .then((result) => {
        let message = i18n.Actions_OneDriveSync_Success;

        if (result.warnings.length > 0) {
          toastWarning(message, result.warnings);
        } else {
          toastSuccess(message);
        }
      })
      .catch((error) => {
        toastError(i18n.Actions_OneDriveSync_Failure, error);
      })
      .finally(() => {
        refetch();
        setIsSubmitting(false);
      });
  }, [meetingId, oneDriveApi, documentActions, i18n, refetch]);

  const isEnabledSync = useMemo(() => {
    const empty = JSON.stringify({});
    const aia = JSON.stringify(agendaItemActions);
    const da = JSON.stringify(documentActions);
    return aia !== empty || da !== empty;
  }, [agendaItemActions, documentActions]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      oneDriveViewModel,
      agendaItems,
      folder,
      isEnabledSync,
      onExecuteSync,
      refetch,
      selectDocumentId,
      setSelectDocumentId,
      agendaItemActions,
      setAgendaItemActions,
      isAdvancedMode,
      setIsAdvancedMode,
      isSubmitting,
      setIsSubmitting,
      setAgendaItemAction,
      documentActions,
      setDocumentActions,
      setDocumentAction,
    }),
    [
      isLoading,
      oneDriveViewModel,
      agendaItems,
      folder,
      isEnabledSync,
      onExecuteSync,
      refetch,
      selectDocumentId,
      setSelectDocumentId,
      agendaItemActions,
      setAgendaItemActions,
      isAdvancedMode,
      setIsAdvancedMode,
      isSubmitting,
      setIsSubmitting,
      setAgendaItemAction,
      documentActions,
      setDocumentActions,
      setDocumentAction,
    ]
  );

  return <OneDriveSyncContext.Provider value={contextValue}>{children}</OneDriveSyncContext.Provider>;
};
